import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Dialog } from '@marketplace/ui';
import { type Quote } from '@marketplace/contracts';
import { quoteTone } from '../../shared/status-tone.js';

/**
 * `W4-T17` §2.2 — what the client can do with the quote they accepted, while the job is `OPEN`: pay
 * the call-out fee and so book the professional (**Award and pay**), or take the acceptance back
 * (**Undo acceptance**). Both ask first, as accepting does.
 *
 * It names no amount. The fee, its IVA and the total are money's numbers and arrive in
 * `booking.create`'s answer, which `CallOutCheckout` shows (`W4-T05` AC23); the dialog only says that
 * the fee is charged now and comes off the price of the job (ADR-013 §4).
 *
 * The component holds which dialog is open and nothing else: the writes are the page's fetchers, so
 * their answers survive the loader re-running after them.
 */
export interface AcceptedQuoteProps {
  quote: Quote;
  busy: boolean;
  /** The booking refusal as a key (`bookingErrorKey`), or `null`. */
  error: string | null;
  onAward: () => void;
  onUndo: () => void;
}

export function AcceptedQuote({
  quote,
  busy,
  error,
  onAward,
  onUndo,
}: AcceptedQuoteProps): ReactElement {
  const { t } = useTranslation();
  const [asking, setAsking] = useState<'award' | 'unaccept' | null>(null);
  const provider = quote.provider.displayName;

  const confirm = (then: () => void) => () => {
    setAsking(null);
    then();
  };

  return (
    <div data-testid={`accepted-${quote.id}`}>
      <p data-testid={`quote-state-${quote.id}`}>
        <Badge tone={quoteTone(quote.status)}>{t(`job.quotes.status.${quote.status}`)}</Badge>
      </p>
      {error === null ? null : <Alert tone="error">{t(error)}</Alert>}
      <p>
        <Button variant="primary" onPress={() => setAsking('award')} isDisabled={busy}>
          {t('job.quotes.award.cta')}
        </Button>{' '}
        <Button variant="secondary" onPress={() => setAsking('unaccept')} isDisabled={busy}>
          {t('job.quotes.unaccept.cta')}
        </Button>
      </p>

      {asking === null ? null : (
        <Dialog
          title={t(`job.quotes.${asking}.confirm.title`)}
          isOpen
          onOpenChange={(open) => {
            if (!open) setAsking(null);
          }}
          footer={
            <>
              <Button variant="secondary" onPress={() => setAsking(null)}>
                {t(`job.quotes.${asking}.confirm.cancel`)}
              </Button>{' '}
              <Button onPress={confirm(asking === 'award' ? onAward : onUndo)}>
                {t(`job.quotes.${asking}.confirm.accept`)}
              </Button>
            </>
          }
        >
          {t(`job.quotes.${asking}.confirm.body`, { provider })}
        </Dialog>
      )}
    </div>
  );
}
